import { formatCents, formatCount, tt } from '@agos/shared'

/**
 * What is metered against what is priced - `SAAS.md` §3.10.
 *
 * **FB-505:** *"the cost side of `Profitability` is read-only, so the margins it reports cannot be
 * corrected."* Nine endpoints with no caller. But the finding behind the screen is not the missing
 * form, it is that **metered and priced are two different lists and nothing shows the gap**.
 *
 * `ProfitabilityService.unitCost` says it in its own javadoc: *"A rule that does not match at all
 * costs nothing, which is how an unpriced service reads on this report: pure profit, **and visibly
 * so**."* It is not visibly so. An unpriced service produces **no line at all** - not a zero
 * anybody could notice, silence. Measured on the owner's central database, 12 368 of franchise 1's
 * 111 251 metered calls have no rule: `fiskalpro`, `bulkgate`, `claude_translate` and `tatrapay`.
 *
 * **And the card fee is aimed at the wrong tenant.** The `tatrapay` rule is `percent_of_volume` at
 * 2 % with `franchise_id = 7`; franchise 7 has zero tatrapay calls and franchise 1's 1 161 match no
 * rule. The fee bills nothing from either side - a rule pointed at the wrong franchise, which is
 * exactly what a screen shows and SQL does not.
 *
 * So this module is a reconciliation, and every figure in it comes from one of the two lists.
 */

/** The modes `ApiPricingService.BILLING_MODES` accepts, and nothing else. */
export const BILLING_MODES = [
  'per_unit',
  'monthly_flat',
  'tiered',
  'percent_of_volume',
  // §3.69 - legacy's fifth, and the owner asked AGOS to follow it: the revenue share is a normal
  // pricing rule now, so it can differ per franchise. Its base is order turnover read from the
  // tenant database, never `api_usage_log` - which is why `percentRuleBillsNothing` below has to
  // leave its service alone.
  'percent_of_revenue',
] as const

/**
 * The service whose base is turnover rather than logged volume - §3.69, `BACKEND.md §10A.181`.
 *
 * <p>Spelled here **and** in `BillingRunner`, which is a duplication accepted on purpose: one side
 * decides what to bill and the other decides what to warn about, and neither can read the other's
 * constant. Each side's comment points at this one.
 */
export const REVENUE_SHARE_SERVICE = 'revenue_share'

export type BillingMode = (typeof BILLING_MODES)[number]

export interface PricingRule {
  id: number
  /** Null means the rule applies to every franchise - the catch-all. */
  franchiseId: number | null
  service: string
  /** Null means any operation within the service. */
  operation: string | null
  billingMode: BillingMode
  priceCentsPerUnit: number
  monthlyFlat: number
  monthlyFlatCost: number
  costCentsPerUnit: number
  freeAllowance: number
  percentRate: number
  priceIncludesVat: boolean
  enabled: boolean
  description: string | null
}

/** One service's calls in the window, out of `GET …/franchises/{id}/usage`'s `totals`. */
export interface ServiceUsage {
  service: string
  calls: number
  success: number
  /**
   * How many of them the upstream refused or never answered - `SAAS.md` §3.89.
   *
   * **The server has answered this on all three groupings the whole time and the screen dropped
   * it**, the same shape as `revenueCents` above: `count(*) filter (where success = false)` is in
   * `totals`, `daily` and `byOperation`. `success` was even parsed here and read by nothing.
   *
   * Measured on the owner's `central_hub`: **137 failures across four services** - `fiskalpro` 104,
   * `bulkgate` 13, `resend` 11, `tatrapay` 9 - none of them visible. A call count rises just as fast
   * when every call fails, so *is this franchise's integration working* had no answer on this page.
   *
   * `claude_translate` reads 0 and that is a separate defect, fixed in `BACKEND.md` §10A.198: it was
   * the one writer of five that decided its own outcome.
   */
  failure: number
  units: number
  /**
   * The money these calls moved, in cents - `SAAS.md` §3.43.
   *
   * **The server has always answered this and the screen dropped it.** Its own words:
   * *"the money a payment call moved, in cents, from the log row's own metadata"*. It is what a
   * `percent_of_volume` rule takes its percentage **of**, so a service that reports nought here can
   * never produce a line however the rule is configured.
   *
   * Measured on the owner's 112 250 logged calls: **only `tatrapay` carries an amount** - 1 154 of
   * its 1 161 - while `google_maps` (93 981), `resend`, `fiskalpro`, `bulkgate` and
   * `claude_translate` carry none.
   */
  revenueCents: number
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/** A numeric column read back through `LegacyRows` arrives as a **string** - `"2.5000"`. */
function num(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function mode(value: unknown): BillingMode {
  return BILLING_MODES.includes(value as BillingMode) ? (value as BillingMode) : 'per_unit'
}

/**
 * One service-and-operation pair in the window - `SAAS.md` §3.105.
 *
 * **Off `byOperation`, which the same payload already carries.** The pricing screen reads the
 * per-franchise usage endpoint for its `totals`; that response also groups by operation, and
 * `PlatformUsageService` has answered it the whole time. So the operation box below offers what the
 * franchise has actually called rather than a blank line: a free-text operation that is mistyped
 * matches nothing, silently, and the difference would only show up in a billing run weeks later.
 */
export interface OperationUsage {
  service: string
  operation: string
  calls: number
}

export function operationUsageOf(payload: unknown): OperationUsage[] {
  const rows = (payload as { byOperation?: unknown } | undefined)?.byOperation
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    const service = text(row.service)
    const operation = text(row.operation)
    // **Both or neither.** `api_usage_log.operation` is NOT NULL and some writers store an empty
    // string, which is not an operation and would offer a blank entry in the list.
    if (service === null || operation === null) return []
    return [{ service, operation, calls: num(row.calls) }]
  })
}

/**
 * The operations seen for one service, most-called first and each once.
 *
 * Ordered by calls rather than alphabetically because the list is a suggestion: the operation
 * somebody wants to price is almost always the busy one. `byOperation` already arrives in that
 * order, and this keeps it rather than restoring it.
 */
export function operationsFor(
  usage: readonly OperationUsage[],
  service: string,
): string[] {
  const seen = new Set<string>()
  for (const row of usage) {
    if (row.service === service) seen.add(row.operation)
  }
  return [...seen]
}

export function pricingRulesOf(payload: unknown): PricingRule[] {
  const wrapped = (payload as { rules?: unknown } | undefined)?.rules
  const rows = Array.isArray(payload) ? payload : Array.isArray(wrapped) ? wrapped : []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        // **Null is meaningful here** and is not an absent field: it is the catch-all rule that
        // applies to every franchise, and reading it as 0 would make it match none.
        franchiseId: typeof row.franchiseId === 'number' ? row.franchiseId : null,
        service: text(row.service) ?? '',
        operation: text(row.operation),
        billingMode: mode(row.billingMode),
        priceCentsPerUnit: num(row.priceCentsPerUnit),
        monthlyFlat: num(row.monthlyFlat),
        monthlyFlatCost: num(row.monthlyFlatCost),
        costCentsPerUnit: num(row.costCentsPerUnit),
        freeAllowance: num(row.freeAllowance),
        percentRate: num(row.percentRate),
        priceIncludesVat: row.priceIncludesVat === true,
        enabled: row.enabled !== false,
        description: text(row.description),
      },
    ]
  })
}

export function serviceUsageOf(payload: unknown): ServiceUsage[] {
  const wrapped = (payload as { totals?: unknown } | undefined)?.totals
  const rows = Array.isArray(payload) ? payload : Array.isArray(wrapped) ? wrapped : []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const row = entry as Record<string, unknown>
    const service = text(row.service)
    if (service === null) return []
    return [
      {
        service,
        calls: num(row.calls),
        success: num(row.success),
        failure: num(row.failure),
        units: num(row.units),
        revenueCents: num(row.revenueCents),
      },
    ]
  })
}

/**
 * Platform-wide usage, as `GET /super-admin/usage/platform` answers it - SAAS.md §3.76.
 *
 * **This endpoint had no caller of any kind.** It is summarised in its own controller as *"The same,
 * across every franchise"*, and `ApiPricingPage` read only the per-franchise one - so the screen was
 * always a view of exactly one tenant while three owner questions (#19, #20, #21) were all
 * cross-franchise ones. The hook census that was supposed to catch this counted 58 hooks of 117,
 * having matched `export const use…` and not the overloaded `export function use…` that orval emits
 * for a query.
 */
export interface PlatformUsage {
  from: string | null
  to: string | null
  byService: PlatformServiceUsage[]
  byFranchise: PlatformFranchiseUsage[]
}

export interface PlatformServiceUsage {
  service: string
  calls: number
  units: number
  revenueCents: number
}

export interface PlatformFranchiseUsage {
  franchiseId: number
  franchiseName: string
  calls: number
  units: number
}

/**
 * The payload, parsed - and every figure comes off it rather than out of a literal.
 *
 * `revenueCents` arrives as a **string** from a numeric column read through `LegacyRows`, which is
 * why `num` exists and why the measured `"0"` for `google_maps` is a zero rather than a falsy
 * surprise.
 */
export function platformUsageOf(payload: unknown): PlatformUsage {
  const root = (payload ?? {}) as Record<string, unknown>
  const window = (root.window ?? {}) as Record<string, unknown>
  return {
    from: text(window.from),
    to: text(window.to),
    byService: rowsOf(root.byService).flatMap((row) => {
      const service = text(row.service)
      if (service === null) return []
      return [
        {
          service,
          calls: num(row.calls),
          units: num(row.units),
          revenueCents: num(row.revenueCents),
        },
      ]
    }),
    byFranchise: rowsOf(root.byFranchise).flatMap((row) => {
      const franchiseName = text(row.franchiseName)
      if (franchiseName === null) return []
      return [
        {
          franchiseId: num(row.franchiseId),
          franchiseName,
          calls: num(row.calls),
          units: num(row.units),
        },
      ]
    }),
  }
}

function rowsOf(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null,
  )
}

/**
 * Services with calls and no revenue, which is #19 and #21 stated as a row rather than as a
 * question.
 *
 * <p>**Not the same as `unpricedServices`.** That one reconciles usage against this franchise's
 * *rules*; this one asks the narrower question the platform view can answer on its own - **money
 * moved, or not** - because `percent_of_volume` takes its percentage of exactly this column, so a
 * service reporting nought here can never produce a line however a rule is configured (§3.43).
 *
 * A service with **no calls** is excluded: nought of nothing is not a finding.
 */
export function servicesWithoutRevenue(
  rows: readonly PlatformServiceUsage[],
): PlatformServiceUsage[] {
  return rows.filter((row) => row.calls > 0 && row.revenueCents === 0)
}

/**
 * What the revenue column says for one service.
 *
 * **`bez pravidla` rather than 0,00 EUR**, for the reason §9.413 needed one screen along: *nought
 * charged* and *no rule matched* are different facts, and a money column showing the first when it
 * means the second is how 94 149 Google calls came to look free.
 */
export function platformRevenueLabel(row: PlatformServiceUsage): string {
  return row.revenueCents === 0 && row.calls > 0 ? tt('bez pravidla') : formatCents(row.revenueCents)
}

/**
 * Services whose metered total is known to be **low**, and why - SAAS.md §3.77.
 *
 * **`google_maps` counted only the address box until the order path was metered** (FB-607,
 * §10A.142). There were two Google clients and one of them recorded nothing, so every Google figure
 * in this repository was the autocomplete alone - and the unmetered one is the client whose volume
 * follows orders, which is the direction that matters when the question is *what should a call cost*.
 *
 * **Keyed on the service rather than on a date.** A hardcoded cut-off would be wrong on production,
 * where the boundary is whenever the owner deployed the fix; naming the cause is true in both
 * environments and stays true. When the meter has been complete long enough for the owner to say so,
 * the entry comes out and this map is empty again.
 */
const UNDERCOUNTED: Record<string, string> = {
  google_maps: tt(
    'do zavedenia merania objednávkovej cesty sa počítalo len vyhľadávanie adresy - skutočný počet je vyšší',
  ),
}

/** Whether this service's total is known to be low, for the marker beside it. */
export function isUndercounted(service: string): boolean {
  return UNDERCOUNTED[service] !== undefined
}

/**
 * The one sentence the panel says underneath, or empty when nothing on it is under-counted.
 *
 * Said once for the panel rather than per row: a caveat repeated on every line reads as decoration,
 * and the rows already carry a marker pointing at it.
 */
export function undercountNote(rows: readonly PlatformServiceUsage[]): string {
  const affected = rows.filter((row) => isUndercounted(row.service)).map((row) => row.service)
  if (affected.length === 0) return ''
  return `${affected.join(', ')}: ${UNDERCOUNTED[affected[0]!]!}`
}

/** The window the figures are for, said on the panel rather than assumed to be *now*. */
export function platformWindowNote(usage: PlatformUsage): string {
  if (usage.from === null || usage.to === null) return tt('za zvolené obdobie')
  return `${skDate(usage.from)} - ${skDate(usage.to)}`
}

function skDate(iso: string): string {
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString('sk-SK')
}

/**
 * A percent rule that cannot ever bill - §3.43.
 *
 * `BillingRunner` computes a `percent_of_volume` line as a percentage of
 * `SUM((metadata->>'amountCents')::bigint)` for the service. So a rule on a service that logs calls
 * and **no amount** is enabled, configured, and worth nought for ever - with nothing on any screen
 * saying why. The owner's own note on the legacy equivalent: *"picking the wrong one for
 * `revenue_share` bills 0 EUR with no error anywhere."*
 *
 * **Three things this deliberately does not fire on.** A rule that is not `percent_of_volume`, since
 * the other modes do not read that column at all. A service with **no usage row**, because that is
 * §3.10's question - *a rule whose franchise has no usage* - and answering it here would let *move it
 * to another franchise* read as a fix for something no franchise can fix. And a service that does
 * report revenue, which is the control: `tatrapay` must not warn.
 */
export function percentRuleBillsNothing(
  rule: PricingRule,
  usage: readonly ServiceUsage[],
): boolean {
  if (rule.billingMode !== 'percent_of_volume') return false
  // §3.69 - **`revenue_share` reports no amount by design.** Its base is order turnover, so this
  // warning would tell the owner a rate cannot be carried on a rule that bills perfectly well. The
  // runner draws the same line: it skips this service in its percent-of-volume block and claims it
  // in the revenue block, whichever of the two modes is stored.
  if (rule.service === REVENUE_SHARE_SERVICE) return false
  const row = usage.find((entry) => entry.service === rule.service)
  if (row === undefined || row.calls === 0) return false
  return row.revenueCents === 0
}

/** Named rather than templated, because the operative fact is *which* service cannot carry a rate. */
export function percentRuleWarning(rule: PricingRule): string {
  return `${tt('Táto služba nezaznamenáva sumu, z ktorej sa dá počítať percento')} - ${rule.service}`
}

/**
 * The rule that would price this call, by the server's own precedence.
 *
 * **Franchise-and-operation, then franchise, then operation, then the catch-all** - `match` in
 * `ProfitabilityService` and `BillingRunner`, both. Copied deliberately rather than simplified to
 * *any rule for this service*: a screen that flattened it would report a franchise as priced by a
 * catch-all while a more specific rule was what actually billed it, and then the figure it showed
 * would be a figure nobody is charged.
 *
 * **`enabled` is not part of the match**, and that is the server's behaviour too: `match` picks the
 * most specific rule and the callers check `enabled` afterwards. So a disabled rule still shadows a
 * catch-all, and a screen that skipped it would say a service was priced when it is not.
 */
export function matchingRule(
  rules: readonly PricingRule[],
  franchiseId: number,
  service: string,
  operation: string | null = null,
): PricingRule | null {
  let ownAndOperation: PricingRule | null = null
  let own: PricingRule | null = null
  let globalAndOperation: PricingRule | null = null
  let global: PricingRule | null = null
  for (const rule of rules) {
    if (rule.service !== service) continue
    const mine = rule.franchiseId !== null && rule.franchiseId === franchiseId
    const everyone = rule.franchiseId === null
    const sameOperation = rule.operation === operation
    const anyOperation = rule.operation === null
    if (mine && sameOperation && ownAndOperation === null) ownAndOperation = rule
    else if (mine && anyOperation && own === null) own = rule
    else if (everyone && sameOperation && globalAndOperation === null) globalAndOperation = rule
    else if (everyone && anyOperation && global === null) global = rule
  }
  return ownAndOperation ?? own ?? globalAndOperation ?? global
}

export interface ReconciledService {
  service: string
  calls: number
  /** How many of the calls the upstream answered usefully - §3.89. */
  success: number
  /** How many it refused or never answered. */
  failure: number
  rule: PricingRule | null
  /** True when calls happened and nothing prices them - the row this screen exists for. */
  unpriced: boolean
  /** True when a rule matched but is switched off, so it bills nothing either. */
  disabled: boolean
}

/**
 * Every metered service in the window, each with the rule that prices it or nothing.
 *
 * Ordered by call count descending, which is the order `totals` already answers in and the order
 * that matters: the biggest unpriced service is the biggest hole.
 */
export function reconcile(
  usage: readonly ServiceUsage[],
  rules: readonly PricingRule[],
  franchiseId: number,
): ReconciledService[] {
  return [...usage]
    .sort((left, right) => right.calls - left.calls)
    .map((row) => {
      const rule = matchingRule(rules, franchiseId, row.service)
      return {
        service: row.service,
        calls: row.calls,
        success: row.success,
        failure: row.failure,
        rule,
        unpriced: rule === null,
        disabled: rule !== null && !rule.enabled,
      }
    })
}

/** The services with calls and no rule, biggest first. */
export function unpricedServices(rows: readonly ReconciledService[]): ReconciledService[] {
  return rows.filter((row) => row.unpriced)
}

/** How many calls are going unpriced, which is the one number this screen is for. */
export function unpricedCalls(rows: readonly ReconciledService[]): number {
  return unpricedServices(rows).reduce((total, row) => total + row.calls, 0)
}

/**
 * Rules for this franchise that no call in the window matched.
 *
 * **The `tatrapay` case.** A 2 % card fee on franchise 7, which has no card volume, bills nothing -
 * and looks configured. Only a rule *naming this franchise* is reported: a catch-all with no usage
 * here is not a mistake, it is a rule for somewhere else.
 */
export function rulesWithoutUsage(
  rules: readonly PricingRule[],
  usage: readonly ServiceUsage[],
  franchiseId: number,
): PricingRule[] {
  const metered = new Set(usage.map((row) => row.service))
  return rules.filter((rule) => rule.franchiseId === franchiseId && !metered.has(rule.service))
}

/** What the reconciliation comes to, in words. */
export function reconciliationNote(rows: readonly ReconciledService[]): string {
  if (rows.length === 0) return ''
  const unpriced = unpricedServices(rows)
  if (unpriced.length === 0) {
    return `${tt('Všetkých')} ${formatCount(rows.length)} ${tt('meraných služieb má pravidlo.')}`
  }
  const names = unpriced.map((row) => row.service).join(', ')
  return `${formatCount(unpricedCalls(rows))} ${tt('volaní bez pravidla')} - ${names}. ${tt('Nefakturuje sa ani nevykazuje ako náklad.')}`
}

/**
 * Said when the window holds nothing, rather than letting an empty list read as *nothing is
 * metered*.
 *
 * **The window defaults to thirty days back** (`PlatformUsageService.DEFAULT_WINDOW`) and the
 * restored data's newest call is 2026-07-28, so the default window is empty on the owner's own
 * database. An empty reconciliation there means *you are looking at the wrong month*, not *every
 * service is priced* - and without this the screen's most reassuring state would be its least
 * informative one.
 */
export function emptyWindowNote(usage: readonly ServiceUsage[], hasRules: boolean): string {
  if (usage.length > 0) return ''
  return hasRules
    ? tt('V tomto období nie je žiadne meranie. Pravidlá existujú - skús iné obdobie.')
    : tt('V tomto období nie je žiadne meranie a nie sú ani pravidlá.')
}

/** What each mode bills, said on the form rather than inferred from which box is filled. */
export function modeNote(billingMode: BillingMode): string {
  if (billingMode === 'per_unit') {
    return tt('Účtuje sa cena za volanie, nad rámec voľných volaní.')
  }
  if (billingMode === 'monthly_flat') {
    return tt('Účtuje sa mesačný paušál. Volania sa neúčtujú zvlášť.')
  }
  if (billingMode === 'tiered') {
    // Traced rather than assumed: `flatLines` charges the flat for `{monthly_flat, tiered}` and
    // `usageLines` skips only `"monthly_flat"`, so a tiered rule gets both. That is a two-part
    // tariff and it is what the owner's `resend` rule is - 20,00/month plus 5 c over 3 000.
    return tt('Účtuje sa paušál aj cena za volanie nad rámec voľných volaní - dvojzložkový tarif.')
  }
  if (billingMode === 'percent_of_revenue') {
    // §3.69 - the fallback said *podiel z objemu* for anything it did not recognise, so a revenue
    // rule would have described itself as a share of volume. The two bases are completely
    // different and the screen is where somebody picks between them.
    return tt('Účtuje sa podiel z tržieb prevádzky za obdobie - nie z objemu volaní.')
  }
  return tt('Účtuje sa podiel z objemu, ktorý službou prešel.')
}

/** Which box a mode actually reads, so the rest can be left alone rather than filled with noughts. */
export function fieldsFor(billingMode: BillingMode): string[] {
  if (billingMode === 'per_unit') return ['priceCentsPerUnit', 'costCentsPerUnit', 'freeAllowance']
  if (billingMode === 'monthly_flat') return ['monthlyFlat', 'monthlyFlatCost']
  if (billingMode === 'tiered') {
    return ['monthlyFlat', 'monthlyFlatCost', 'priceCentsPerUnit', 'costCentsPerUnit', 'freeAllowance']
  }
  return ['percentRate']
}

export type RateProblem = 'not-a-number' | 'negative' | 'over-100' | null

/**
 * Why a rate cannot be saved, or null.
 *
 * The server's own rule, stated before the request: every money field `>= 0`, and `percentRate` no
 * more than 100. A percentage above the whole charges more than the volume it is a share of.
 */
export function rateProblem(raw: string, field: string): RateProblem {
  const value = Number(raw.trim().replace(',', '.'))
  if (raw.trim() === '' || !Number.isFinite(value)) return 'not-a-number'
  if (value < 0) return 'negative'
  if (field === 'percentRate' && value > 100) return 'over-100'
  return null
}

export function rateProblemMessage(problem: RateProblem): string {
  if (problem === null) return ''
  if (problem === 'not-a-number') return tt('Musí to byť číslo')
  if (problem === 'negative') return tt('Nemôže byť záporné - faktúra by dostala mínusový riadok')
  return tt('Podiel nemôže byť viac ako 100 %')
}

/**
 * Whether a cost of nought is worth questioning for this many calls.
 *
 * **`google_maps` is the case:** `monthly_flat` 20,00 charged with `monthly_flat_cost` 0,00 and
 * `cost_cents_per_unit` 0, against 93 392 calls. The profitability report reads that as pure
 * margin, and `BLOCKED.md` records that Google bills per call. Not refused - a genuinely free
 * service exists - but not left silent either.
 */
export function zeroCostNote(row: ReconciledService): string {
  const rule = row.rule
  if (rule === null || row.calls === 0) return ''
  const costed = rule.costCentsPerUnit > 0 || rule.monthlyFlatCost > 0
  if (costed) return ''
  return `${tt('Nákladová strana je nulová pri')} ${formatCount(row.calls)} ${tt('volaniach - v ziskovosti to vyzerá ako čistá marža.')}`
}

/**
 * How a service's calls went, in the words legacy's own usage page used - `SAAS.md` §3.89.
 *
 * **Legacy drew calls, units, success, failure and a failure-rate percentage.** This screen drew a
 * call count. The percentage is the part that makes a figure actionable: 9 failures out of 1 161
 * `tatrapay` calls is a bank having a bad afternoon, and 9 out of 9 is a franchise whose credentials
 * are wrong, and the two look identical as a bare count.
 *
 * Returns an empty string when nothing was logged, so a period with no calls says nothing rather
 * than *0 % failed*.
 */
export function outcomeNote(row: Pick<ReconciledService, 'calls' | 'success' | 'failure'>): string {
  if (row.calls === 0) return ''
  // The two do not have to add up to `calls` for this to be readable, and asserting that they do
  // would be a claim about a SQL `filter` rather than about the product. Drawn as given.
  if (row.failure === 0) {
    return `${formatCount(row.success)} ${tt('v poriadku')}`
  }
  return `${formatCount(row.success)} ${tt('v poriadku')}, ${formatCount(row.failure)} ${tt('zlyhalo')} (${failureRate(row)})`
}

/**
 * The share that failed, to one decimal place.
 *
 * <p>Out of `calls` rather than out of `success + failure`: `calls` is `count(*)` over the same
 * rows, so if the two ever disagree the honest denominator is the total.
 */
export function failureRate(row: Pick<ReconciledService, 'calls' | 'failure'>): string {
  if (row.calls === 0) return ''
  const share = (row.failure / row.calls) * 100
  // A single failure in a hundred thousand calls is not 0,0 %: it rounds to it, and a rate printed
  // as zero beside a non-zero count reads as a bug in the screen.
  const shown = share > 0 && share < 0.1 ? '<0,1' : share.toFixed(1).replace('.', ',')
  return `${shown} %`
}

/**
 * The services whose failures are worth saying out loud, worst share first.
 *
 * **Ordered by share rather than by count**, which is the whole point: `fiskalpro`'s 104 failures
 * out of 4 602 calls is 2,3 % and ordinary, while a franchise with 9 of 9 has an integration that
 * has never worked. Ordering by count puts the ordinary one first.
 */
export function failingServices(rows: readonly ReconciledService[]): ReconciledService[] {
  return rows
    .filter((row) => row.failure > 0)
    .sort((left, right) => right.failure / right.calls - left.failure / left.calls)
}

/** The sentence above the list, or empty when everything answered. */
export function failureSummary(rows: readonly ReconciledService[]): string {
  const failing = failingServices(rows)
  if (failing.length === 0) return ''
  const total = failing.reduce((sum, row) => sum + row.failure, 0)
  const names = failing.map((row) => `${row.service} ${failureRate(row)}`).join(', ')
  return `${formatCount(total)} ${tt('neúspešných volaní')} - ${names}.`
}
